import React, { useEffect, useRef, useState } from 'react';
import { ViewerPropertiesComplete } from '../model';
import classNames from 'classnames';
import { useLocalStorageBackedState } from './common/3d/useLocalStoreBackedState';
import { z } from 'zod';

type Content =
  | {
      status: null;
      content: null;
      src: string | null;
    }
  | {
      status: 'loading';
      content: null;
      src: string | null;
    }
  | {
      status: 'error';
      content: null;
      src: string | null;
    }
  | { status: 'ready'; content: string; src: string };

const Body: React.FC<{ content: Content; mono: boolean; wrap: boolean }> = ({
  content,
  mono,
  wrap,
}) => {
  if (content.status !== 'ready') {
    return <span className="MediaText">{content.status}</span>;
  }
  const split = content.content.split('\n');
  return (
    <div className={classNames('MediaText', { 'MediaText--mono': mono, 'MediaText--wrap': wrap })}>
      {split.map((line, lineNumber) => {
        return (
          <React.Fragment key={lineNumber}>
            <span className="MediaText__lineNumber">{lineNumber + 1}</span>
            <span className="MediaText__content">{line}</span>
          </React.Fragment>
        );
      })}
    </div>
  );
};
export const MediaText: React.FC<ViewerPropertiesComplete> = ({ src, captureMode }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState<Content>({ content: null, status: null, src: null });
  const [mono, setMono] = useLocalStorageBackedState(
    true,
    z.boolean(),
    'File viewer, text mode, monospace font',
  );
  const [wrap, setWrap] = useLocalStorageBackedState(
    true,
    z.boolean(),
    'File viewer, text mode, word wrapping',
  );
  useEffect(() => {
    const controller = new AbortController();
    const loadText = async () => {
      setText({ status: 'loading', content: null, src });
      try {
        const response = await fetch(src, { signal: controller.signal });

        if (!response.ok) {
          if (!controller.signal.aborted) setText({ status: 'error', content: null, src });
          return;
        }

        const content = await response.text();
        if (!controller.signal.aborted) setText({ status: 'ready', content, src });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        if (!controller.signal.aborted) setText({ status: 'error', content: null, src });
      }
    };

    void loadText();

    return () => {
      controller.abort();
    };
  }, [src]);

  const current: Content = text.src === src ? text : { status: 'loading', content: null, src };
  return (
    <div
      data-preview-state={current.status ?? 'loading'}
      data-preview-type="text"
      data-preview-error={current.status === 'error' ? 'source-load-error' : undefined}
      className="gen-file-viewer__text-container gen-file-viewer__container"
      ref={containerRef}
    >
      <Body mono={mono} wrap={wrap} content={current} />
      {!captureMode && (
        <div className="gen-file-viewer__controls">
          <div className="gen-file-viewer__controls-button-group">
            <div onClick={() => setMono(!mono)} className="gen-file-viewer__controls-button">
              {mono ? 'Monospace' : 'Proportional'}
            </div>
          </div>
          <div className="gen-file-viewer__controls-button-group">
            <div onClick={() => setWrap(!wrap)} className="gen-file-viewer__controls-button">
              {wrap ? 'Wrapping' : 'Not wrapping'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
